import { useEffect, useState, useRef } from "react";
import { useParams } from "react-router-dom"; // To access dynamic route parameters
import axios from "axios";
import config from "../../config";
import * as tus from "tus-js-client";
import CryptoJS from "crypto-js"; // Import the crypto-js library

const EventForm = () => {
  const [fileNames, setFileNames] = useState("No files uploaded yet.");
  const [formData, setFormData] = useState({
    person_name: "",
    person_email_id: "",
    person_city: "",
    caption: "",
    media: null, // Media will be stored as a File object
  });
  const { eventUrlSlug } = useParams(); // Get the event_url_slug from the URL
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const videoFileUploadRef = useRef(null); // Reference for the file input
  const [uploadProgress, setUploadProgress] = useState(0); // Track progress
  const [isUploading, setIsUploading] = useState(false); // Track if upload is ongoing

  // fetch content - start

  useEffect(() => {
    const fetchEvent = async () => {
      try {
        const response = await axios.get(
          `${config.apiBaseUrl}/api/events/event-by-slug/${eventUrlSlug}`
        );
        setEvent(response.data);
        setLoading(false);
      } catch (error) {
        console.error("Error fetching event details", error);
        setLoading(false);
      }
    };

    fetchEvent();
  }, [eventUrlSlug]); // Rerun this effect when eventUrlSlug changes

  if (loading) {
    return <div>Loading...</div>;
  }

  if (!event) {
    return <div>Event not found!</div>;
  }

  // fetch content - end

  const resetForm = () => {
    setFormData({
      person_name: "",
      person_email_id: "",
      person_city: "",
      caption: "",
      media: null, // Media will be stored as a File object
    });
  };

  // Handle file selection
  const handleFileChange = (e) => {
    const files = e.target.files;
    if (files.length > 0) {
      setFileNames(
        `Files Selected: ${Array.from(files)
          .map((file) => file.name)
          .join(", ")}`
      );
      setFormData((prevState) => ({
        ...prevState,
        media: files[0], // Save the first selected file
      }));
    }
  };

  // Handle drag-and-drop
  const handleDrop = (e) => {
    e.preventDefault();
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      setFileNames(
        `Files Selected: ${Array.from(files)
          .map((file) => file.name)
          .join(", ")}`
      );
      setFormData((prevState) => ({
        ...prevState,
        media: files[0], // Save the first dropped file
      }));
    }
  };

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  // New function to upload large video file
  const uploadLargeVideo = async (videoId, presigned_signature) => {
    const library_id = "366380";
    const expiration_time = Math.floor(new Date().getTime() / 1000) + 200000;

    const file = formData.media; // Access the file selected for upload

    // Set upload state to true when upload starts
    setIsUploading(true);

    const videoFileUpload = new tus.Upload(file, {
      endpoint: "https://video.bunnycdn.com/tusupload",
      retryDelays: [0, 3000, 5000, 10000, 20000, 60000, 60000],
      headers: {
        AuthorizationSignature: presigned_signature,
        AuthorizationExpire: expiration_time,
        VideoId: videoId,
        LibraryId: library_id,
      },
      metadata: {
        filetype: file.type,
      },
      onError: function (error) {
        console.error("Failed because: " + error);
        setIsUploading(false); // Reset upload state on error
      },
      onProgress: function (bytesUploaded, bytesTotal) {
        const percentage = ((bytesUploaded / bytesTotal) * 100).toFixed(2);
        setUploadProgress(percentage); // Update progress
        // document.getElementById("add_video_progressbar_id").style.width = `${percentage}%`;
        // document.getElementById("add_video_progressbar_id").innerHTML = `${percentage}%`;
        // Update progress bar here with React state or refs
        console.log(bytesUploaded, bytesTotal, percentage + "%");
      },
      onSuccess: function () {
        console.log("Upload success: " + videoFileUpload.url);
        setIsUploading(false); // Reset upload state on error
        // Video upload is complete, now proceed with submitting the form
        handleSubmitAfterUpload(videoId, "video");
        // You can handle success actions here, like updating the UI or notifying the user
      },
    });

    videoFileUpload.findPreviousUploads().then(function (previousUploads) {
      if (previousUploads.length) {
        videoFileUpload.resumeFromPreviousUpload(previousUploads[0]);
      }

      videoFileUpload.start();
    });
  };

  const handleSubmitAfterUpload = async (videoId, media_type) => {
    const formToSubmit = new FormData();
    formToSubmit.append("event_id", event.id); // Add event ID from fetched event
    formToSubmit.append("person_name", formData.person_name);
    formToSubmit.append("person_email_id", formData.person_email_id);
    formToSubmit.append("person_city", formData.person_city);
    formToSubmit.append("caption", formData.caption);
    formToSubmit.append("media_type", media_type);

    if (media_type == "video") {
      formToSubmit.append("video_id", videoId);
    } else {
      formToSubmit.append("media", formData.media); // Add media file (image/video)
    }

    try {
      const response = await axios.post(
        `${config.apiBaseUrl}/api/events/add-event-post`,
        formToSubmit,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      window.notifyMessage(
        "Success",
        "Your post has been submitted successfully. It will appear on the wall after approval.",
        "success"
      );
      setIsSubmitting(false);
      resetForm();
    } catch (error) {
      // Check if it's an error from the API response
      if (
        error.response &&
        error.response.data &&
        error.response.data.message
      ) {
        window.notifyMessage(
          "Error",
          error.response.data.message, // Display specific error message from backend
          "danger"
        );
        setIsSubmitting(false);
      } else {
        // Handle generic errors (e.g., network errors)
        window.notifyMessage("Error", "There was an error!", "danger");
        setIsSubmitting(false);
      }
    }
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    setIsSubmitting(true);

    // Validate required fields
    if (
      !formData.person_name ||
      !formData.person_email_id ||
      !formData.person_city ||
      !formData.caption ||
      !formData.media
    ) {
      window.notifyMessage(
        "Error",
        "All fields are mandatory! Please fill in all the fields.",
        "danger"
      );
      setIsSubmitting(false);
      return; // Prevent form submission if any field is missing
    }

    // If the media is a video, fetch the video ID
    let videoId = null;
    let presigned_signature = null;
    let media_type = "image";
    if (formData.media.type.startsWith("video/")) {
      media_type = "video";
      try {
        const videoResponse = await axios.get(
          `${config.apiBaseUrl}/api/events/get-video-id/`,
          {
            params: { event_id: event.id },
          }
        );

        videoId = videoResponse.data.video_id; // Get the video ID from the response
        presigned_signature = videoResponse.data.presigned_signature; // Get the video ID from the response

        // After getting the video ID, proceed with the large video upload
        await uploadLargeVideo(videoId, presigned_signature);
      } catch (error) {
        window.notifyMessage(
          "Error",
          "Failed to fetch video ID. Please try again.",
          "danger"
        );
        setIsSubmitting(false);
        return; // Stop submission if there is an error fetching the video ID
      }
    } else {
      // If it's not a video, proceed directly to submit the form
      handleSubmitAfterUpload(null, "image");
    }
    // setIsSubmitting(false);
    // console.log({ videoId });
    // return;

    // // Prepare form data to be sent to the server
    // const formToSubmit = new FormData();
    // formToSubmit.append("event_id", event.id); // Add event ID from fetched event
    // formToSubmit.append("person_name", formData.person_name);
    // formToSubmit.append("person_email_id", formData.person_email_id);
    // formToSubmit.append("person_city", formData.person_city);
    // formToSubmit.append("caption", formData.caption);
    // formToSubmit.append("media_type", media_type);

    // if (formData.media.type.startsWith("video/")) {
    //   formToSubmit.append("video_id", videoId);
    // } else {
    //   formToSubmit.append("media", formData.media); // Add media file (image/video)
    // }

    // try {
    //   // Send the form data via POST request
    //   const response = await axios.post(
    //     `${config.apiBaseUrl}/api/events/add-event-post`,
    //     formToSubmit,
    //     { headers: { "Content-Type": "multipart/form-data" } } // Ensure the request is sent as form-data
    //   );
    //   // console.log("Post submitted successfully:", response.data);

    //   // Notify event on successful event addition
    //   window.notifyMessage(
    //     "Success",
    //     "Post submitted successfully:",
    //     "success"
    //   );

    //   setIsSubmitting(false);

    //   // // Call resetForm to reset all the form fields
    //   // resetForm();

    //   // Optionally show a success message or redirect user
    // } catch (error) {
    //   // Check if it's an error from the API response
    //   if (
    //     error.response &&
    //     error.response.data &&
    //     error.response.data.message
    //   ) {
    //     window.notifyMessage(
    //       "Error",
    //       error.response.data.message, // Display specific error message from backend
    //       "danger"
    //     );
    //     setIsSubmitting(false);
    //   } else {
    //     // Handle generic errors (e.g., network errors)
    //     window.notifyMessage("Error", "There was an error!", "danger");
    //     setIsSubmitting(false);
    //   }
    // }
  };

  // Handle file dropzone click (to trigger file input)
  const handleDropzoneClick = (e) => {
    // Prevent triggering dropzone click if the click was on the label (with id 'choose_file_label_button')
    if (e.target.id === "choose_file_label_button") {
      e.stopPropagation();
      return;
    }
    document.getElementById("upload").click(); // Trigger file input click
  };

  return (
    <div className="container social-wall-form-main d-flex justify-content-center p-2">
      <div
        className="card shadow mt-5"
        style={{ maxWidth: "600px", width: "100%" }}
      >
        <div className="card-body">
          <div className="text-center mb-4">
            <img
              src="../icon/sidebar-icon.png"
              alt="navbar brand"
              className="navbar-brand"
              height="45"
            />
          </div>
          <form onSubmit={handleSubmit}>
            <div
              className="upload-dropzone mb-3"
              onDragOver={(e) => e.preventDefault()}
              onDragLeave={() => {}}
              onDrop={handleDrop}
              onClick={handleDropzoneClick} // Add onClick here to trigger file input click
            >
              <i className="fas fa-upload upload-icon"></i>
              <p>Drag & Drop your image/video here or</p>
              <input
                type="file"
                className="position-absolute invisible"
                id="upload"
                accept="image/*, video/*"
                onChange={handleFileChange}
              />
              <label
                className="btn-upload"
                htmlFor="upload"
                id="choose_file_label_button"
              >
                Choose File
              </label>
              <div id="file-names" className="file-names">
                {fileNames}
              </div>
            </div>

            <div className="mb-3">
              <label htmlFor="person_name" className="form-label">
                Name
              </label>
              <input
                type="text"
                className="form-control"
                id="person_name"
                name="person_name"
                placeholder="Enter your name"
                value={formData.person_name}
                onChange={handleInputChange}
                required
              />
            </div>

            <div className="mb-3">
              <label htmlFor="person_email_id" className="form-label">
                Email
              </label>
              <input
                type="email"
                className="form-control"
                id="person_email_id"
                name="person_email_id"
                placeholder="Enter your email address"
                value={formData.person_email_id}
                onChange={handleInputChange}
                required
              />
            </div>

            <div className="mb-3">
              <label htmlFor="person_city" className="form-label">
                City
              </label>
              <input
                type="text"
                className="form-control"
                id="person_city"
                name="person_city"
                placeholder="Enter city name"
                value={formData.person_city}
                onChange={handleInputChange}
                required
              />
            </div>

            <div className="mb-3">
              <label htmlFor="caption" className="form-label">
                Caption This
              </label>
              <textarea
                rows="4"
                className="form-control"
                id="caption"
                name="caption"
                placeholder="Enter caption"
                value={formData.caption}
                onChange={handleInputChange}
                required
              />
            </div>

            {/* Progress Bar */}
            <div className={`progress mb-3 ${isUploading ? "" : "d-none"}`}>
              <div
                className="progress-bar"
                id="add_video_progressbar_id"
                role="progressbar"
                style={{ width: `${uploadProgress}%` }}
                aria-valuemin="0"
                aria-valuemax="100"
              >
                {uploadProgress}%
              </div>
            </div>

            <button
              type="submit"
              className="btn btn-blue-custom w-100"
              disabled={isSubmitting} // Disable the button while submitting
            >
              {isSubmitting ? (
                <span
                  className="spinner-border spinner-border-sm"
                  role="status"
                  aria-hidden="true"
                ></span>
              ) : (
                "Submit"
              )}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default EventForm;
